import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import type {
  IntegrationMappingRowRequest,
  IntegrationReconciliationDatasetRowDto,
  IntegrationReconciliationDataTypeDto,
  IntegrationReconciliationResultDto,
  IntegrationReconciliationRunDto,
  IntegrationReconciliationRunRowDto,
} from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { UploadFileButton } from "@/components/Button/UploadFileButton";
import { downloadExcel, parseExcel } from "@/components/DataTable/sheet";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Download01Icon } from "@/components/Icons/Download01Icon";
import { Upload01Icon } from "@/components/Icons/Upload01Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Accordion } from "@/design-system/Accordion";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { Notice } from "@/design-system/Notice";
import { SectionCard } from "@/design-system/SectionCard";
import { Stack } from "@/design-system/Stack";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { integrationMutations } from "@/queries/integrations/mutations";
import { useIntegrationQueries } from "@/queries/integrations/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

const ADDRESS_HEADERS = {
  id: "AoP address ID",
  name: "Address",
  location: "Building and city",
  primary: "REMS address ID",
  unlinkPrimary: "Unlink REMS address (yes/no)",
} as const;

const RESIDENT_HEADERS = {
  id: "AoP resident ID",
  name: "Resident",
  email: "Email",
  location: "Address",
  primary: "REMS contact ID",
  secondary: "REMS contract ID",
  unlinkPrimary: "Unlink REMS contact (yes/no)",
  unlinkSecondary: "Unlink REMS contract (yes/no)",
} as const;

/** Shows REMS mapping health, reconciliation entry points, and recent runs for an integration. */
export function RemsReconciliationSection({ isActive }: { isActive: boolean }): React.ReactNode {
  const { t } = useTranslation();
  const integrationQueries = useIntegrationQueries();
  const { data: runs, error: runsError } = useQuery(integrationQueries.remsReconciliationRuns());

  return (
    <Stack gap="md">
      <header>
        <h2 className="text-heading-2">{t("page.integration.rems.reconciliation.title")}</h2>
        <p className="mt-space-xs max-w-measure-reading text-body text-text-secondary">
          {t("page.integration.rems.reconciliation.description")}
        </p>
      </header>

      {!isActive && <Notice variant="special" message={t("page.integration.rems.reconciliation.inactive-notice")} />}

      <Stack gap="md">
        <MappingStatusCard dataType="addresses" />
        <MappingStatusCard dataType="residents" />
      </Stack>

      {runsError ? (
        <Notice variant="danger" message={t("page.integration.rems.reconciliation.load-failed")} />
      ) : (
        <RunHistory runs={runs ?? []} />
      )}
    </Stack>
  );
}

/** Renders the address or resident reconciliation workflow for an authorized REMS manager. */
export function RemsReconciliationPage({
  dataType,
}: {
  dataType: IntegrationReconciliationDataTypeDto;
}): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const hasPermission = usePermission();
  const integrationQueries = useIntegrationQueries();
  const { data: config, isFetching, error } = useQuery(integrationQueries.remsConfiguration());

  if (!hasPermission((x) => x.integrations.canManageRems)) {
    return <ErrorPage status={404} />;
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  if (isFetching) {
    return <FullSizeLoader withPadding />;
  }

  const remsName = t("page.integration.rems.title");
  const title = t(`page.integration.rems.reconciliation.${dataType}.title`);
  const isActive = !!config?.contracts?.enabled && !!config.contracts.syncContacts;

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={title}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            { name: t("page.integration.title"), to: routes.projects.integrations({ slug }) },
            { name: remsName, to: routes.projects.integrationRems({ slug }) },
            { name: title },
          ]}
        />
      }
    >
      <Stack gap="md">
        {!isActive && <Notice variant="special" message={t("page.integration.rems.reconciliation.inactive-notice")} />}
        <MappingImportCard dataType={dataType} />
      </Stack>
    </DocumentPage>
  );
}

function MappingStatusCard({ dataType }: { dataType: IntegrationReconciliationDataTypeDto }): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const integrationQueries = useIntegrationQueries();
  const { data: dataset, error } = useQuery(integrationQueries.remsReconciliationData(dataType));
  const { mappedCount, unmappedCount } = getMappingCounts(dataType, dataset?.rows ?? []);
  const href =
    dataType === "addresses"
      ? routes.projects.integrationRemsAddresses({ slug })
      : routes.projects.integrationRemsResidents({ slug });

  return (
    <SectionCard
      title={t(`page.integration.rems.reconciliation.${dataType}.title`)}
      description={t(`page.integration.rems.reconciliation.${dataType}.summary`)}
      renderBadges={() => (
        <>
          <Badge variant="success">{t("page.integration.rems.reconciliation.mapped", { count: mappedCount })}</Badge>
          {unmappedCount > 0 && (
            <Badge variant="warning">
              {t("page.integration.rems.reconciliation.unmapped", { count: unmappedCount })}
            </Badge>
          )}
        </>
      )}
      renderAction={() => (
        <Button type="link" href={href} variant="soft">
          {t("page.integration.rems.reconciliation.manage")}
        </Button>
      )}
    >
      {error && <Notice variant="danger" message={t("page.integration.rems.reconciliation.load-failed")} />}
    </SectionCard>
  );
}

function MappingImportCard({ dataType }: { dataType: IntegrationReconciliationDataTypeDto }): React.ReactNode {
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const integrationQueries = useIntegrationQueries();
  const { data: dataset, isPending, error } = useQuery(integrationQueries.remsReconciliationData(dataType));
  const previewMutation = integrationMutations.usePreviewRemsReconciliation();
  const applyMutation = integrationMutations.useApplyRemsReconciliation();
  const [preview, setPreview] = useState<IntegrationReconciliationResultDto>();
  const [previewRows, setPreviewRows] = useState<IntegrationMappingRowRequest[]>([]);
  const [fileName, setFileName] = useState<string>();
  const [workbookError, setWorkbookError] = useState(false);

  const rows = dataset?.rows ?? [];
  const { mappedCount, unmappedCount } = getMappingCounts(dataType, rows);
  const title = t(`page.integration.rems.reconciliation.${dataType}.title`);

  const downloadTemplate = async () => {
    if (dataType === "addresses") {
      await downloadExcel(`rems-address-mappings-${sessionUser.project.name}`, [
        Object.values(ADDRESS_HEADERS),
        ...rows.map((row) => [row.entityId, row.displayName, row.location ?? "", row.primaryExternalId ?? "", ""]),
      ]);

      return;
    }

    await downloadExcel(`rems-resident-mappings-${sessionUser.project.name}`, [
      Object.values(RESIDENT_HEADERS),
      ...rows.map((row) => [
        row.entityId,
        row.displayName,
        row.email ?? "",
        row.location ?? "",
        row.primaryExternalId ?? "",
        row.secondaryExternalId ?? "",
        "",
        "",
      ]),
    ]);
  };

  const uploadWorkbook = async (file?: File) => {
    if (!file || !dataset) {
      return;
    }

    setWorkbookError(false);
    let requestRows: IntegrationMappingRowRequest[];
    try {
      const workbookRows = (await parseExcel(await file.arrayBuffer())) as Record<string, unknown>[];
      requestRows = createRequestRows(dataType, workbookRows, dataset.rows);
    } catch {
      setWorkbookError(true);

      return;
    }

    try {
      const result = await previewMutation.mutateAsync({
        dataType,
        data: { rows: requestRows },
      });
      setFileName(file.name);
      setPreviewRows(requestRows);
      setPreview(result);
    } catch {
      // The mutation displays the user-facing failure notification.
    }
  };

  const apply = async () => {
    if (!preview) {
      return;
    }

    try {
      const result = await applyMutation.mutateAsync({
        dataType,
        data: {
          rows: previewRows,
        },
      });
      setPreview(result);
    } catch {
      // The mutation displays the user-facing failure notification.
    }
  };

  return (
    <SectionCard
      title={title}
      description={t(`page.integration.rems.reconciliation.${dataType}.description`)}
      renderBadges={() => (
        <>
          <Badge variant="success">{t("page.integration.rems.reconciliation.mapped", { count: mappedCount })}</Badge>
          {unmappedCount > 0 && (
            <Badge variant="warning">
              {t("page.integration.rems.reconciliation.unmapped", { count: unmappedCount })}
            </Badge>
          )}
        </>
      )}
    >
      {error && <Notice variant="danger" message={t("page.integration.rems.reconciliation.load-failed")} />}
      {workbookError && <Notice variant="danger" message={t("page.integration.rems.reconciliation.workbook-failed")} />}

      <Inline gap="sm">
        <Button
          variant="ghostPrimary"
          icon={<Download01Icon />}
          disabled={isPending || !dataset}
          onClick={() => void downloadTemplate()}
        >
          {t("page.integration.rems.reconciliation.download-template")}
        </Button>
        <UploadFileButton
          variant="soft"
          icon={<Upload01Icon />}
          accept=".xlsx,.xls"
          disabled={isPending || !dataset || previewMutation.isPending || applyMutation.isPending}
          onChange={(event) => {
            void uploadWorkbook(event.target.files?.[0]);
            event.target.value = "";
          }}
        >
          {t("page.integration.rems.reconciliation.upload")}
        </UploadFileButton>
      </Inline>

      {fileName && <p className="text-caption text-text-secondary">{fileName}</p>}
      {preview && (
        <ReconciliationReview
          dataType={dataType}
          result={preview}
          onApply={() => void apply()}
          isApplying={applyMutation.isPending}
        />
      )}
    </SectionCard>
  );
}

function ReconciliationReview({
  dataType,
  result,
  onApply,
  isApplying,
}: {
  dataType: IntegrationReconciliationDataTypeDto;
  result: IntegrationReconciliationResultDto;
  onApply: () => void;
  isApplying: boolean;
}): React.ReactNode {
  const { t } = useTranslation();

  return (
    <section className="flex flex-col gap-space-sm border-t border-border-default pt-space-md">
      <Inline gap="sm">
        <Badge variant={result.changedCount > 0 ? "success" : "default"}>
          {t("page.integration.rems.reconciliation.changes", { count: result.changedCount })}
        </Badge>
        {result.failedCount > 0 && (
          <Badge variant="danger">
            {t("page.integration.rems.reconciliation.failed", { count: result.failedCount })}
          </Badge>
        )}
        {result.warningCount > 0 && (
          <Badge variant="warning">
            {t("page.integration.rems.reconciliation.warnings", { count: result.warningCount })}
          </Badge>
        )}
      </Inline>

      {result.rows.length === 0 ? (
        <Notice variant="special" message={t("page.integration.rems.reconciliation.no-changes")} />
      ) : (
        <ResultRows dataType={dataType} rows={result.rows} />
      )}

      {!result.applied && (
        <Button onClick={onApply} isLoading={isApplying} disabled={result.changedCount === 0}>
          {t("page.integration.rems.reconciliation.apply", { count: result.changedCount })}
        </Button>
      )}
      {result.applied && <Notice variant="success" message={t("page.integration.rems.reconciliation.applied")} />}
    </section>
  );
}

function ResultRows({
  dataType,
  rows,
}: {
  dataType: IntegrationReconciliationDataTypeDto;
  rows: (IntegrationReconciliationResultDto["rows"][number] | IntegrationReconciliationRunRowDto)[];
}): React.ReactNode {
  const slug = useSlug();
  const { t } = useTranslation();

  return (
    <div className="max-h-scroll-area-compact overflow-auto rounded-sm border border-border-default">
      <table className="w-full text-left text-caption">
        <thead className="sticky top-0 bg-background-secondary text-caption-emphasis">
          <tr>
            <th className="p-space-sm">{t("page.integration.rems.reconciliation.table.row")}</th>
            <th className="p-space-sm">{t("page.integration.rems.reconciliation.table.record")}</th>
            <th className="p-space-sm">{t("page.integration.rems.reconciliation.table.status")}</th>
            <th className="p-space-sm">{t("page.integration.rems.reconciliation.table.details")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.rowNumber}-${row.entityId}`} className="border-t border-border-default">
              <td className="p-space-sm align-top">{row.rowNumber}</td>
              <td className="p-space-sm align-top">
                {row.entityLabel && row.entityId ? (
                  <Link
                    className="text-text-brand underline"
                    to={
                      dataType === "addresses"
                        ? routes.addresses.edit({ slug, id: row.entityId })
                        : routes.users.details({ slug, id: row.entityId })
                    }
                  >
                    {row.entityLabel}
                  </Link>
                ) : (
                  row.entityLabel || row.entityId
                )}
              </td>
              <td className="p-space-sm align-top">
                <StatusBadge status={row.status} />
              </td>
              <td className="p-space-sm align-top text-text-secondary">{row.message ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RunHistory({ runs }: { runs: IntegrationReconciliationRunDto[] }): React.ReactNode {
  const { t } = useTranslation();

  return (
    <SectionCard
      title={t("page.integration.rems.reconciliation.history.title")}
      description={t("page.integration.rems.reconciliation.history.description")}
    >
      {runs.length === 0 ? (
        <p className="text-body text-text-secondary">{t("page.integration.rems.reconciliation.history.empty")}</p>
      ) : (
        <Accordion.Root>
          {runs.map((run) => (
            <Accordion.Item key={run.id} value={run.id}>
              <Accordion.Trigger>
                <span className="flex flex-wrap items-center gap-space-sm">
                  <span>{new Date(run.completedAt).toLocaleString()}</span>
                  <Badge variant={run.failedCount > 0 ? "warning" : "success"}>
                    {t("page.integration.rems.reconciliation.history.summary", {
                      updated: run.updatedCount,
                      failed: run.failedCount,
                    })}
                  </Badge>
                  <span className="text-caption text-text-secondary">
                    {t(`page.integration.rems.reconciliation.${run.dataType}.title`)} ·{" "}
                    {run.initiatedBy ?? "Area of People"}
                  </span>
                </span>
              </Accordion.Trigger>
              <Accordion.Content>
                <section className="flex flex-col gap-space-sm pt-space-sm">
                  <ResultRows dataType={run.dataType} rows={run.rows} />
                  <Button variant="ghostPrimary" icon={<Download01Icon />} onClick={() => void downloadRun(run)}>
                    {t("page.integration.rems.reconciliation.history.download")}
                  </Button>
                </section>
              </Accordion.Content>
            </Accordion.Item>
          ))}
        </Accordion.Root>
      )}
    </SectionCard>
  );
}

function StatusBadge({
  status,
}: {
  status: IntegrationReconciliationResultDto["rows"][number]["status"];
}): React.ReactNode {
  const { t } = useTranslation();

  return <Badge variant={getStatusVariant(status)}>{t(`page.integration.rems.reconciliation.status.${status}`)}</Badge>;
}

function getStatusVariant(
  status: IntegrationReconciliationResultDto["rows"][number]["status"],
): "danger" | "warning" | "default" | "success" {
  switch (status) {
    case "failed":
      return "danger";
    case "warning":
      return "warning";
    case "unchanged":
      return "default";
    default:
      return "success";
  }
}

function getMappingCounts(
  dataType: IntegrationReconciliationDataTypeDto,
  rows: IntegrationReconciliationDatasetRowDto[],
): { mappedCount: number; unmappedCount: number } {
  const mappedCount = rows.filter(
    (row) => row.primaryExternalId && (dataType === "addresses" || row.secondaryExternalId),
  ).length;

  return { mappedCount, unmappedCount: rows.length - mappedCount };
}

function createRequestRows(
  dataType: IntegrationReconciliationDataTypeDto,
  workbookRows: Record<string, unknown>[],
  datasetRows: IntegrationReconciliationDatasetRowDto[],
): IntegrationMappingRowRequest[] {
  const currentById = new Map(datasetRows.map((row) => [row.entityId, row]));

  return workbookRows.flatMap((row, index) => {
    const headers = dataType === "addresses" ? ADDRESS_HEADERS : RESIDENT_HEADERS;
    const entityId = readCell(row, headers.id);
    const current = currentById.get(entityId);
    const primaryExternalId = readCell(row, headers.primary) || undefined;
    const secondaryExternalId =
      dataType === "residents" ? readCell(row, RESIDENT_HEADERS.secondary) || undefined : undefined;
    const unlinkPrimary = readBoolean(row, headers.unlinkPrimary);
    const unlinkSecondary = dataType === "residents" && readBoolean(row, RESIDENT_HEADERS.unlinkSecondary);
    const primaryChanged = unlinkPrimary || (!!primaryExternalId && primaryExternalId !== current?.primaryExternalId);
    const secondaryChanged =
      dataType === "residents" &&
      (unlinkSecondary || (!!secondaryExternalId && secondaryExternalId !== current?.secondaryExternalId));

    if (current && !primaryChanged && !secondaryChanged) {
      return [];
    }

    return [
      {
        rowNumber: index + 2,
        entityId,
        primaryExternalId,
        secondaryExternalId,
        unlinkPrimary,
        unlinkSecondary,
      },
    ];
  });
}

function readCell(row: Record<string, unknown>, header: string): string {
  const value = row[header];
  if (typeof value !== "string" && typeof value !== "number" && typeof value !== "boolean") {
    return "";
  }

  return String(value).trim();
}

function readBoolean(row: Record<string, unknown>, header: string): boolean {
  return ["1", "true", "yes", "y", "x"].includes(readCell(row, header).toLowerCase());
}

async function downloadRun(run: IntegrationReconciliationRunDto): Promise<void> {
  await downloadExcel(`rems-${run.dataType}-run-${run.id}`, [
    ["Workbook row", "AoP ID", "Record", "Status", "REMS ID", "REMS contract ID", "Issue"],
    ...run.rows.map((row) => [
      String(row.rowNumber),
      row.entityId ?? "",
      row.entityLabel,
      row.status,
      row.primaryExternalId ?? "",
      row.secondaryExternalId ?? "",
      row.message ?? "",
    ]),
  ]);
}
